import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import ispaiLogo from '@/assets/ispai-logo-mark.png';
import { getSession, saveSession, clearSession } from '@/utils/sessionStorage';

interface PitchDeckPasswordFormProps {
  onUnlock: (campaignName?: string) => void;
}

interface PitchDeckSession {
  email: string;
  campaign_name: string;
  expires_at: string;
  is_admin?: boolean;
}

const PITCH_DECK_SESSION_KEY = 'pitch_deck_session';

// List of all active campaign codes to try
const CAMPAIGN_CODES = ['ispai2026', 'han2026'];

export const getPitchDeckSession = (): PitchDeckSession | null => {
  const session = getSession<PitchDeckSession>(PITCH_DECK_SESSION_KEY);
  if (!session) return null;
  return session;
};

export const clearPitchDeckSession = () => {
  clearSession(PITCH_DECK_SESSION_KEY);
};

const PitchDeckPasswordForm = ({ onUnlock }: PitchDeckPasswordFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Check for existing session on mount
  useEffect(() => {
    const existingSession = getPitchDeckSession();
    if (existingSession) {
      onUnlock(existingSession.campaign_name);
    }
  }, [onUnlock]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Missing Information",
        description: "Please enter both email and password.",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // First, try admin authentication (no campaign code needed)
      const { data: adminData, error: adminError } = await supabase.functions.invoke('verify-campaign-password', {
        body: {
          email: email.trim().toLowerCase(),
          campaign_code: '', // Empty for admin check
          password: password
        }
      });

      if (!adminError && adminData?.success && adminData?.is_admin) {
        // Admin access granted - no logging
        if (rememberMe) {
          saveSession(PITCH_DECK_SESSION_KEY, {
            email: email.trim().toLowerCase(),
            campaign_name: 'Admin Access',
            is_admin: true,
          }, true);
        }
        toast({
          title: "Admin Access Granted",
          description: "Welcome! You have admin access to the iSPAI Pitch Deck.",
        });
        onUnlock('Admin Access');
        return;
      }

      // Not an admin, try campaign passwords
      let verified = false;
      let matchedCampaignName = '';

      for (const campaignCode of CAMPAIGN_CODES) {
        const { data, error } = await supabase.functions.invoke('verify-campaign-password', {
          body: {
            email: email.trim().toLowerCase(),
            campaign_code: campaignCode,
            password: password
          }
        });

        if (!error && data?.success) {
          verified = true;
          matchedCampaignName = data.campaign_name || campaignCode;
          
          // Also log to pitch_deck_access_logs with campaign tracking
          await supabase
            .from('pitch_deck_access_logs')
            .insert({
              email: email.trim().toLowerCase(),
              campaign_code: campaignCode,
            });
          
          break;
        }
      }

      if (verified) {
        // Save session if rememberMe is checked
        if (rememberMe) {
          saveSession(PITCH_DECK_SESSION_KEY, {
            email: email.trim().toLowerCase(),
            campaign_name: matchedCampaignName,
          }, true);
        }
        toast({
          title: "Access Granted",
          description: `Welcome to the iSPAI Pitch Deck via ${matchedCampaignName}.`,
        });
        onUnlock(matchedCampaignName);
      } else {
        toast({
          title: "Access Denied",
          description: "The password you entered is incorrect.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      toast({
        title: "Error",
        description: "An error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <img src={ispaiLogo} alt="iSPAI" className="w-16 h-16 object-contain" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Protected Content</h2>
          <p className="text-gray-400">
            Enter your email and password to access the iSPAI Pitch Deck.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pitch-email" className="text-gray-300">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="pitch-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pitch-password" className="text-gray-300">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="pitch-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="pitch-rememberMe"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(checked === true)}
              className="border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-black"
            />
            <label
              htmlFor="pitch-rememberMe"
              className="text-sm font-medium text-gray-300 cursor-pointer"
            >
              Remember me for 10 days
            </label>
          </div>
          
          {rememberMe && (
            <p className="text-xs text-gray-500">
              Your session will persist on this device
            </p>
          )}

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-white text-black hover:bg-gray-200"
          >
            {isSubmitting ? 'Verifying...' : 'Access Pitch Deck'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PitchDeckPasswordForm;
